import { type ReactNode } from "react";
import BulkATCPopupContext from "~/features/bulkAddToCart/components/BulkATCPopupContext";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useTranslation, type WithTranslation } from "react-i18next";
import { type MenuProps as MUIMenuProps } from "~/common/components/Menu/Menu";
import { MENU_POS_BELOW } from "~/common/components/Menu/MenuPositions";
import {
  AB_MONITOR_PAGE,
  CONTRACT_OVERVIEW_PAGE,
  ORDER_HISTORY_PAGE,
  QUOTE_HISTORY_PAGE,
} from "~/utils/url/staticUrls";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "~/common/mui";
import { C_ON_SURFACE_VARIANT } from "~/common/components/icon/Colors";
import QuoteSplitPopupContext from "~/features/QuoteSplit/components/QuoteSplitPopupContext";
import { useSession } from "~/app/providers/SessionProvider";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { downloadUsersReport } from "~/features/navigation/api/downloadUsersReport";

interface MenuVisibilityContext {
  quotationSplitEnabled: boolean;
}

interface MenuProps extends MUIMenuProps {
  onClose: () => void;
  beforeOptionsChildren?: ReactNode;
  children?: never;
}

interface MenuEntry {
  permisisonCheck: (context: MenuVisibilityContext) => boolean;
  factory: (
    t: WithTranslation["t"],
    closeMenu: () => void,
    openBulkPopup?: () => void,
    openQuoteSplitPopup?: () => void,
    bulkUploadDisabled?: boolean,
  ) => JSX.Element;
}

export const entriesMenu: MenuEntry[] = [
  {
    permisisonCheck: () => userHasPermission(Permission.SEE_ABMONITOR),
    factory: (t, closeMenu) => (
      <MenuItem
        className={"title-medium"}
        // @ts-ignore
        component={"a"}
        href={AB_MONITOR_PAGE}
        key={"abmonitor"}
        data-test-id={"header-btn-abmonitor"}
        onClick={closeMenu}
      >
        <ListItemIcon>
          <MenuBookOutlinedIcon sx={{ color: C_ON_SURFACE_VARIANT }} />
        </ListItemIcon>
        <ListItemText>{t("header.link.ordersDropdown.abmonitor")}</ListItemText>
      </MenuItem>
    ),
  },
  {
    permisisonCheck: () => userHasPermission(Permission.SEARCH_ORDERS),
    factory: (t, closeMenu) => (
      <MenuItem
        className={"title-medium"}
        // @ts-ignore
        component={"a"}
        href={ORDER_HISTORY_PAGE}
        key={"orderHistory"}
        data-test-id={"header-btn-order-history"}
        onClick={closeMenu}
      >
        <ListItemIcon>
          <InventoryOutlinedIcon sx={{ color: C_ON_SURFACE_VARIANT }} />
        </ListItemIcon>
        <ListItemText>{t("header.link.ordersDropdown.orders")}</ListItemText>
      </MenuItem>
    ),
  },
  {
    permisisonCheck: () => userHasPermission(Permission.SEARCH_QUOTES),
    factory: (t, closeMenu) => (
      <MenuItem
        className={"title-medium"}
        // @ts-ignore
        component={"a"}
        href={QUOTE_HISTORY_PAGE}
        key={"searchQuotes"}
        data-test-id={"header-btn-quote-history"}
        onClick={closeMenu}
      >
        <ListItemIcon>
          <RequestQuoteOutlinedIcon sx={{ color: C_ON_SURFACE_VARIANT }} />
        </ListItemIcon>
        <ListItemText>{t("header.link.quotes")}</ListItemText>
      </MenuItem>
    ),
  },
  {
    permisisonCheck: ({ quotationSplitEnabled }) => quotationSplitEnabled,
    factory: (t, closeMenu, openBulkPopup, openQuoteSplitPopup) => (
      <MenuItem
        onClick={() => {
          openQuoteSplitPopup?.();
          closeMenu();
        }}
        key={"quoteSplit"}
        data-test-id={"header-btn-quote-split"}
      >
        <ListItemIcon>
          <CallSplitOutlinedIcon sx={{ color: C_ON_SURFACE_VARIANT, transform: "rotate(90deg)" }} />
        </ListItemIcon>
        <ListItemText>{t("header.link.quoteSplit")}</ListItemText>
      </MenuItem>
    ),
  },
  {
    permisisonCheck: () => userHasPermission(Permission.BULK_UPLOAD),
    factory: (t, closeMenu, openBulkPopup, _openQuoteSplitPopup, bulkUploadDisabled) => (
      <MenuItem
        onClick={() => {
          openBulkPopup?.();
          closeMenu();
        }}
        key={"bulkAdd"}
        data-test-id={"header-btn-bulk-upload"}
        disabled={bulkUploadDisabled}
      >
        <ListItemIcon>
          <NoteAddOutlinedIcon sx={{ color: C_ON_SURFACE_VARIANT }} />
        </ListItemIcon>
        <ListItemText>{t("header.link.ordersDropdown.bulkAdd")}</ListItemText>
      </MenuItem>
    ),
  },
  {
    permisisonCheck: () => userHasPermission(Permission.VIEW_CONTRACT),
    factory: (t, closeMenu) => (
      <MenuItem
        className={"title-medium"}
        // @ts-ignore
        component={"a"}
        href={CONTRACT_OVERVIEW_PAGE}
        key={"contractOverview"}
        data-test-id={"header-btn-contract-list"}
        onClick={closeMenu}
      >
        <ListItemIcon>
          <HistoryEduOutlinedIcon sx={{ color: C_ON_SURFACE_VARIANT }} />
        </ListItemIcon>
        <ListItemText>{t("header.link.ordersDropdown.contractOverview")}</ListItemText>
      </MenuItem>
    ),
  },
  {
    permisisonCheck: () => userHasPermission(Permission.DOWNLOAD_USER_REPORT),
    factory: (t, closeMenu) => (
      <MenuItem
        onClick={() => {
          void downloadUsersReport();
          closeMenu();
        }}
        key={"usersReport"}
        data-test-id={"header-btn-users-report"}
      >
        <ListItemIcon>
          <FileDownloadOutlinedIcon sx={{ color: C_ON_SURFACE_VARIANT }} />
        </ListItemIcon>
        <ListItemText>{t("header.link.ordersDropdown.usersReport")}</ListItemText>
      </MenuItem>
    ),
  },
];

export const MyCompanyMenu = ({ onClose, beforeOptionsChildren, ...props }: MenuProps) => {
  const { t } = useTranslation();
  const quotationSplitEnabled = useSession()?.commerceContext?.quotationSplitEnabled ?? false;
  const visibleMenuEntries = entriesMenu.filter((item) => item.permisisonCheck({ quotationSplitEnabled }));

  if (!visibleMenuEntries.length) {
    return null;
  }

  return (
    <BulkATCPopupContext>
      {({ disabled: bulkUploadDisabled, openPopup: openBulkPopup }) => (
        <QuoteSplitPopupContext>
          {({ openPopup: openQuoteSplitPopup }) => (
            <Menu onClose={onClose} {...MENU_POS_BELOW} disableAutoFocusItem {...props}>
              {beforeOptionsChildren}

              {visibleMenuEntries.map((item) =>
                item.factory(t, onClose, openBulkPopup, openQuoteSplitPopup, bulkUploadDisabled),
              )}
            </Menu>
          )}
        </QuoteSplitPopupContext>
      )}
    </BulkATCPopupContext>
  );
};
